
import React, { useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { styled } from '@mui/material/styles';
import { Table, TableBody, TableCell, TableHead, TableFooter, TableRow, Paper, Typography, Grid } from '@mui/material';

import { formatMoney } from '../../shared/formatters';
import { loadStart } from '../../redux/futureBalanceSlice';
import Filter from './filter';
import FutureIncome from './futureIncome';

const RootDiv = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(3),
  flexGrow: 1,
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    margin: '-15px',
    marginTop: theme.spacing(2),
    width: 'calc(100% + 30px)',
  },
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  overflow: 'hidden',
}));

const StyledTable = styled(Table)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    minWidth: 'auto',
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  cursor: 'pointer',
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(0, 98, 105, 0.04)',
  },
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: 'rgba(0, 98, 105, 0.05)',
  '& .MuiTableCell-head': {
    fontWeight: 700,
    color: theme.palette.text.primary,
    fontSize: '0.875rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontSize: '0.938rem',
  fontWeight: 500,
  borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
}));

const MoneyCell = styled(TableCell)(({ theme }) => ({
  fontSize: '0.938rem',
  fontWeight: 500,
  padding: '16px',
  whiteSpace: 'nowrap',
  borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
}));

const SubTitle = styled(Typography)(({ theme }) => ({
  padding: theme.spacing(3, 3, 2, 3),
  fontWeight: 700,
  color: '#1a1a1a',
}));


function Report() {
  const dispatch = useDispatch();
  
  // Redux state selectors
  const shouldBeUpdated = useSelector(state => state.futureBalance.shouldBeUpdated);
  const lastUpdate = useSelector(state => state.futureBalance.lastUpdate);
  const result = useSelector(state => state.futureBalance.result);
  
  // Redux actions
  const loadStartAction = useCallback(() => dispatch(loadStart()), [dispatch]);

  useEffect(() => {
    if (shouldBeUpdated || new Date(lastUpdate).getTime() + 1000 * 10 < new Date().getTime()) {
      loadStartAction();
    }
  }, [shouldBeUpdated, lastUpdate, loadStartAction]);

  if (!result.lastBalance)
    return <div>No data</div>;

  const balanceMonths = Object.keys(result.currentBalance);
  return (
    <>
      <Typography variant="h4" component="h2" sx={{ 
        fontWeight: 700,
        color: '#1a1a1a',
        marginBottom: 3,
      }}>
        Saldo Futuro
      </Typography>
      <Filter></Filter>
      <RootDiv>
        <Grid container spacing={{ xs: 0, sm: 2, md: 3 }}>
          <Grid item size={{ xs: 12, sm: 12, md: 4 }}  sx={{ paddingY: { xs: 1, sm: 0 } }}>
            <StyledPaper>
              <SubTitle component="h2" variant="h5">
                Saldo atual
              </SubTitle>
              <StyledTable>
                <StyledTableHead>
                  <TableRow>
                    <StyledTableCell>
                      Data
                    </StyledTableCell>
                    <StyledTableCell align="right">
                      Diferença
                    </StyledTableCell>
                    <StyledTableCell align="right">
                      Saldo
                  </StyledTableCell>
                  </TableRow>
                </StyledTableHead>
                <TableBody>
                  <StyledTableRow hover>
                    <StyledTableCell scope="row">
                      Saldo inicial
                      </StyledTableCell>
                    <MoneyCell align="right">{formatMoney(0)}</MoneyCell>
                    <MoneyCell align="right">{formatMoney(result.previousBalance)}</MoneyCell>
                  </StyledTableRow>
                  {(balanceMonths || []).map(balanceMonth =>
                    <StyledTableRow hover key={balanceMonth}>
                      <StyledTableCell scope="row">
                        {balanceMonth}
                      </StyledTableCell>
                      <MoneyCell align="right">{formatMoney(result.currentBalance[balanceMonth].difference)}</MoneyCell>
                      <MoneyCell align="right">{formatMoney(result.currentBalance[balanceMonth].balance)}</MoneyCell>
                    </StyledTableRow>
                  )}
                </TableBody>
                <TableFooter>
                  <StyledTableRow hover>
                    <StyledTableCell sx={{ fontWeight: 700, fontSize: '1rem' }}>
                      Média no período
                    </StyledTableCell>
                    <MoneyCell align="right" sx={{ fontWeight: 700, fontSize: '1rem' }}>{formatMoney(result.averageDifference)}</MoneyCell>
                    <MoneyCell align="right" sx={{ fontWeight: 700, fontSize: '1rem' }}>{formatMoney(result.lastBalance)}</MoneyCell>
                  </StyledTableRow>
                </TableFooter>
              </StyledTable>
            </StyledPaper>
          </Grid>
          <Grid item size={{ xs: 12, sm: 12, md: 8 }}  sx={{ paddingY: { xs: 1, sm: 0 } }}>
            <StyledPaper>
              <FutureIncome
                monthlyIncreaseRate='0.6'
                currentValue={result.lastBalance}
                averageDifference={result.averageDifference}
                numberOfYears='10'
              ></FutureIncome>
            </StyledPaper>
          </Grid>
        </Grid>
      </RootDiv>
    </>
  );
}

export default Report;
